import { Link, useParams } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import ServiceIcon from '../components/ServiceIcon';
import FAQSection from '../components/FAQSection';
import useSEO from '../hooks/useSEO';
import useJsonLd from '../hooks/useJsonLd';
import { SITE_CONFIG } from '../data/seoConfig';
import { SERVICE_FAQS } from '../data/faqs';
import { SERVICES } from '../mock';

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  const canonical = service ? `${SITE_CONFIG.url}/services/${service.slug}` : undefined;

  useSEO(
    service
      ? {
          title: `${service.title} Agency — Ransen Digital Marketing`,
          description: `${service.short} Ransen delivers ${service.title.toLowerCase()} as part of a full-service digital marketing program. Book a free strategy call.`,
          canonical,
          keywords: `${service.title.toLowerCase()}, ${service.title.toLowerCase()} agency, ${service.title.toLowerCase()} services, digital marketing agency, ${service.slug.replace(/-/g, ' ')}`,
        }
      : {}
  );

  const serviceFaqs = service ? SERVICE_FAQS[service.slug] || [] : [];

  useJsonLd(
    service ? `service-detail-schema-${service.slug}` : 'service-detail-schema',
    service
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'Service',
            name: service.title,
            description: service.long,
            url: canonical,
            serviceType: service.title,
            provider: { '@id': `${SITE_CONFIG.url}/#organization` },
            areaServed: { '@type': 'Place', name: 'Worldwide' },
            hasOfferCatalog: {
              '@type': 'OfferCatalog',
              name: `${service.title} outcomes`,
              itemListElement: service.outcomes.map((o) => ({
                '@type': 'Offer',
                itemOffered: { '@type': 'Service', name: o },
              })),
            },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_CONFIG.url },
              { '@type': 'ListItem', position: 2, name: 'Services', item: `${SITE_CONFIG.url}/services` },
              { '@type': 'ListItem', position: 3, name: service.title, item: canonical },
            ],
          },
        ]
      : null
  );

  if (!service) {
    return (
      <div className='pt-40 pb-32 text-center'>
        <h1 className='text-3xl text-white'>Service not found</h1>
        <Link to='/services' className='mt-6 inline-block text-violet-300'>Back to services</Link>
      </div>
    );
  }

  const related = SERVICES.filter((s) => s.slug !== slug).slice(0, 3);

  return (
    <div>
      <PageHeader
        eyebrow='Service'
        title={<><span className='text-brand-gradient'>{service.title}</span></>}
        description={service.short}
        crumbs={[{ label: 'Services', to: '/services' }, { label: service.title }]}
      />

      <section className='py-16'>
        <div className='max-w-6xl mx-auto px-6 grid lg:grid-cols-3 gap-10'>
          <div className='lg:col-span-2'>
            <div className='w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500/20 to-fuchsia-500/10 border border-violet-500/25 flex items-center justify-center text-violet-200 mb-6'>
              <ServiceIcon name={service.icon} className='w-6 h-6' />
            </div>
            <h2 className='font-heading text-3xl md:text-4xl font-bold text-white leading-tight'>How Ransen delivers {service.title.toLowerCase()}</h2>
            <p className='mt-5 text-white/70 text-[16px] leading-relaxed'>{service.long}</p>

            <h3 className='mt-12 font-heading text-2xl font-semibold text-white'>What you can expect</h3>
            <div className='mt-5 grid sm:grid-cols-2 gap-3'>
              {service.outcomes.map((o) => (
                <div key={o} className='glass-card rounded-xl p-4 flex items-center gap-3'>
                  <div className='w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center'>
                    <Check className='w-4 h-4 text-white'/>
                  </div>
                  <div className='text-white/85 text-[14px] font-medium'>{o}</div>
                </div>
              ))}
            </div>

            <h3 className='mt-12 font-heading text-2xl font-semibold text-white'>How we work</h3>
            <div className='mt-5 space-y-4'>
              {[
                { t: 'Diagnose', d: 'Audit your current program, tracking, funnel, and channel data. Identify constraints.' },
                { t: 'Build', d: `Design a custom ${service.title} program with clear KPIs, forecasts, and creative brief.` },
                { t: 'Launch', d: 'Deploy campaigns, tracking, and reporting. Establish weekly optimization cadence.' },
                { t: 'Optimize', d: 'Weekly experiments, monthly reallocation, quarterly forecasts — forever compounding.' },
              ].map((step, i) => (
                <div key={step.t} className='flex gap-4'>
                  <div className='w-9 h-9 shrink-0 rounded-full border border-violet-500/40 bg-violet-500/10 flex items-center justify-center text-violet-200 font-heading font-semibold text-sm'>{i+1}</div>
                  <div>
                    <div className='text-white font-semibold text-[15px]'>{step.t}</div>
                    <div className='text-white/55 text-[14px] mt-0.5'>{step.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <aside className='lg:sticky lg:top-28 lg:self-start'>
            <div className='glass-card rounded-2xl p-6'>
              <div className='text-[11px] tracking-[0.2em] uppercase text-violet-200 font-semibold'>Get Started</div>
              <h3 className='mt-3 font-heading text-2xl font-bold text-white'>Book a free {service.title} audit</h3>
              <p className='mt-3 text-white/60 text-[14px] leading-relaxed'>30-min diagnostic call with a senior strategist. Global time zones covered.</p>
              <Link to='/contact' className='mt-5 btn-primary text-white text-sm font-semibold px-5 py-2.5 rounded-full inline-flex items-center gap-2 w-full justify-center'>
                Book a Strategy Call <ArrowRight className='w-4 h-4' />
              </Link>
            </div>
            <div className='mt-5 glass-card rounded-2xl p-6'>
              <div className='text-white/70 text-sm font-semibold mb-3'>Explore related</div>
              <div className='space-y-2'>
                {related.map((r) => (
                  <Link key={r.slug} to={`/services/${r.slug}`} className='flex items-center justify-between text-white/70 hover:text-white text-[14px] py-1.5'>
                    <span>{r.title}</span>
                    <ArrowRight className='w-3.5 h-3.5'/>
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </section>

      {serviceFaqs.length > 0 && (
        <FAQSection
          items={serviceFaqs}
          schemaId={`service-faq-${service.slug}`}
          eyebrow={service.title}
          title={`${service.title} — FAQ`}
          subtitle={`Answers to the most common questions about ${service.title.toLowerCase()} programs at Ransen.`}
        />
      )}
    </div>
  );
}
